import React, { useState } from 'react';
import { Button, Input, Modal, Text } from '@asigna/kit';
import { AddressBookDialog, InputField } from 'components';
import ContactsIcon from 'images/contacts.svg';
import { useAtom } from 'jotai';
import { uniqBy, uniqueId } from 'lodash';
import { contactsAtom } from 'store/contacts';
import { IContact, Owner } from 'types';
import { isValidRecipient } from 'utils';

import { OwnerItem } from '../../OwnerItem';
import { MAX_OWNERS_PER_SAFE } from '../constants';

interface StepTwoProps {
  owners: Owner[];
  setOwners: React.Dispatch<React.SetStateAction<Owner[]>>;
  safeContacts: IContact[];
  confirmationThreshold: number;
  walletAccount: string;
  onConfirmationThresholdChange: (data: number) => void;
  onNextClick: () => void;
  onBackClick: () => void;
}

export const StepTwo: React.FC<StepTwoProps> = ({
  owners,
  setOwners,
  safeContacts,
  confirmationThreshold,
  walletAccount,
  onConfirmationThresholdChange,
  onNextClick,
  onBackClick,
}) => {
  const [isAddressBookOpened, setAddressBookOpened] = useState(false);
  const [contacts] = useAtom(contactsAtom);
  const [isInputFieldOpened, setInputFieldOpened] = useState(false);
  const [ownersCountError, setOwnersCountError] = useState('');
  const [ownerErrorAddress, setOwnerErrorAddress] = useState('');
  const [ownerAddress, setOwnerAddress] = useState('');

  const handleAddFromAddressBookConfirm = (addedContacts: IContact[]) => {
    const ownersNotFromContacts = owners.filter(
      (owner) => !contacts?.find((contact) => contact.contact_address === owner.address)
    );
    const updatedOwners = [
      ...ownersNotFromContacts,
      ...addedContacts.map((addedContact) => {
        return {
          name: addedContact.contact_name,
          address: addedContact.contact_address,
        };
      }),
    ];
    setOwners(updatedOwners);
  };

  const isDuplicateAddress = (address: string) => {
    return !!owners.find((owner) => owner.address === address);
  };

  const saveOwner = (ownerAddress: string) => {
    const newName =
      contacts?.find((contact) => ownerAddress === contact.contact_address)?.contact_name ||
      `Wallet`;

    if (isDuplicateAddress(ownerAddress)) {
      return;
    }
    setOwners((previousOwners) => [
      ...previousOwners,
      {
        name:
          newName === `Wallet`
            ? `Wallet ${owners.filter((item) => item.name?.startsWith('Wallet ')).length + 1}`
            : newName,
        address: ownerAddress,
      },
    ]);
  };
  const handleOwnersCountChange: React.ComponentProps<typeof InputField>['onChange'] = (e) => {
    const value = e.target.value;
    onConfirmationThresholdChange(parseInt(value));

    if (parseInt(value) > owners.length) {
      onConfirmationThresholdChange(owners.length);
      setOwnersCountError('');

      return;
    }
    if (parseInt(value) < 1) {
      onConfirmationThresholdChange(1);
      setOwnersCountError('');

      return;
    }
    if (isNaN(parseInt(value)) || parseInt(value) > owners.length || parseInt(value) < 1) {
      setOwnersCountError('Invalid confirmation number');

      return;
    }
    setOwnersCountError('');
  };

  const canContinue = () => {
    if (owners.length < 1) {
      return false;
    }

    if (owners.length > MAX_OWNERS_PER_SAFE) {
      return false;
    }

    if (confirmationThreshold <= 0) {
      return false;
    }

    if (uniqBy(owners, 'address')?.length !== owners.length) {
      return false;
    }

    if (Boolean(ownerErrorAddress)) {
      return false;
    }

    return true;
  };

  const onEditOwnerName = (contact: Owner) => {
    const newOwners = owners.map((a) => {
      return a.address === contact.address ? { ...a, name: contact.name } : a;
    });

    setOwners(newOwners);
    // contactId === ''
    //   ? addContact(contact.name, contact.address).then(() => getContacts())
    //   : editContact(contact.name, contact.address, contactId).then(() => getContacts());
    // contactId === '' &&
    //   addNotification({
    //     title: 'Contact Added',
    //     message: 'New contact record added.',
    //     type: 'success',
    //   });
  };

  return (
    <div className="flex flex-col gap-24 max-w-[592px] w-full mx-auto">
      <div className="flex flex-col gap-8">
        <Text message="You can add up to 10 owners" color="white" size="r20" />
        <Text
          message="* Vault owners and the confirmation threshold cannot be changed after the vault is created."
          color="primary"
          size="r12"
        />
      </div>
      <div className="flex flex-col gap-24 rounded-[12px] border border-[.5px] border-black-4 bg-black-2 p-16 sm:p-24 mb-32">
        <Text message="Added Owners" size="m14" color="white" />
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-12">
            {owners.map((owner, index) => (
              <OwnerItem
                key={uniqueId('owner.address')}
                owner={owner}
                index={index}
                removeOwner={(ownerToRemove: Owner) => {
                  const newOwners = owners.filter((x) => x !== ownerToRemove);
                  setOwners(newOwners);
                }}
                onEditOwnerName={onEditOwnerName}
                walletAccount={walletAccount}
              />
            ))}
            <Input
              value={ownerAddress}
              placeholder="Enter address here"
              type="text"
              onChange={(e) => {
                setOwnerErrorAddress('');
                setOwnerAddress(e.target.value);
              }}
              size="xs"
              className="h-36"
              rightButton={
                ownerAddress.length === 0 && safeContacts.length > 0 ? (
                  <button
                    className="flex items-center justify-center"
                    onClick={() => setAddressBookOpened(true)}
                    disabled={owners.length >= MAX_OWNERS_PER_SAFE}
                  >
                    <img src={ContactsIcon} width={16} height={16} alt="contacts" />
                  </button>
                ) : ownerAddress.length > 0 ? (
                  <Button
                    size="sm"
                    colorType="inherit"
                    onClick={() => {
                      if (owners.length >= MAX_OWNERS_PER_SAFE) {
                        return;
                      }
                      if (ownerAddress.length > 0 && !isValidRecipient(ownerAddress)) {
                        setOwnerErrorAddress('Invalid address');

                        return false;
                      }
                      setOwnerAddress('');
                      saveOwner(ownerAddress);
                      setInputFieldOpened(false);
                    }}
                    disabled={
                      !ownerAddress ||
                      ownerErrorAddress !== '' ||
                      owners.length >= MAX_OWNERS_PER_SAFE
                    }
                    message="Save"
                    fontSize="r13"
                    fontColor={
                      !ownerAddress ||
                      ownerErrorAddress !== '' ||
                      owners.length >= MAX_OWNERS_PER_SAFE
                        ? 'regular'
                        : 'primary'
                    }
                    className="!px-0 active:outline-none focus-visible:outline-none"
                  />
                ) : (
                  <></>
                )
              }
            />
            {Boolean(ownerErrorAddress) && (
              <p className="text-r12 text-error">{ownerErrorAddress}</p>
            )}

            {isInputFieldOpened ? (
              <div className="flex gap-16">
                <Button
                  size="sm"
                  colorType="secondary"
                  onClick={() => {
                    setOwnerAddress('');
                    setOwnerErrorAddress('');
                    setInputFieldOpened(false);
                  }}
                  message="Cancel"
                />
                <Button
                  size="sm"
                  onClick={() => {
                    if (owners.length >= MAX_OWNERS_PER_SAFE) {
                      return;
                    }
                    setOwnerAddress('');
                    saveOwner(ownerAddress);
                    setInputFieldOpened(false);
                  }}
                  disabled={
                    !ownerAddress ||
                    ownerErrorAddress !== '' ||
                    owners.length >= MAX_OWNERS_PER_SAFE
                  }
                  message="Save"
                />
              </div>
            ) : (
              <>
                {owners.length >= MAX_OWNERS_PER_SAFE && (
                  <div className="flex flex-col gap-12 mt-8">
                    <p className="text-r12 text-error">
                      The maximum number of owners is already added
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className="h-[.5px] bg-black-4 mx-[-24px]" />

        <div className="flex flex-col gap-8">
          <Text message="Confirmation threshold" color="white" size="m16" />
          <Text message="Number of owners to confirm any transaction" color="regular" size="m14" />
          <div className="flex items-center gap-8 mt-8">
            <InputField
              value={confirmationThreshold}
              type="number"
              onChange={handleOwnersCountChange}
              errorInfo={ownersCountError}
            />
            <Text message={`out of ${owners.length} owners`} color="white" size="m16" />
          </div>
        </div>

        <div className="flex gap-16 justify-end">
          <Button
            colorType="secondary"
            onClick={onBackClick}
            message="Back"
            className="w-[128px]"
          />
          <Button
            colorType="primary"
            onClick={onNextClick}
            disabled={!canContinue() || owners.length > MAX_OWNERS_PER_SAFE || owners.length < 1}
            message="Next"
            className="w-[128px]"
          />
        </div>
      </div>

      {/* TODO_R: переместить в AddNewOwnerAddress */}
      <Modal onClose={() => setAddressBookOpened(false)} isShow={isAddressBookOpened}>
        <AddressBookDialog
          handleClose={() => setAddressBookOpened(false)}
          onConfirm={handleAddFromAddressBookConfirm}
          passedInContacts={owners}
        />
      </Modal>
    </div>
  );
};
